// @systeme: contrat
// Les éléments : tâches, événements, notes. Une seule table, un seul contrat.
import { z } from 'zod';
import { SchemaCategorieElement, SchemaOptionReport, SchemaOrigineElement, SchemaSensibilite, SchemaStatutElement, SchemaTypeElement, SchemaVisibilite, } from '../enumerations.js';
/**
 * Un instant, au format ISO 8601 avec décalage horaire, produit par le serveur.
 * Le client ne fabrique jamais de date lui-même : il renvoie ce qu'il a reçu ou ce que
 * l'utilisatrice a choisi dans un sélecteur.
 */
export const SchemaInstant = z.iso.datetime({ offset: true });
/** Un jour civil, au format AAAA-MM-JJ, en Europe/Paris. */
export const SchemaJour = z.iso.date();
/** Une heure « HH:MM », telle que la donne un sélecteur d'heure. */
export const SchemaHeure = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, { error: 'Cette heure ne ressemble à rien.' });
const SchemaTitre = z.string().trim().min(1, { error: 'Il manque un titre.' }).max(200);
export const SchemaCreationElement = z.object({
    type: SchemaTypeElement.default('tache'),
    titre: SchemaTitre,
    detail: z.string().trim().max(5000).optional(),
    categorie: SchemaCategorieElement.optional(),
    /**
     * Le jour choisi. Nul = « sans date ». Absent = le serveur choisit (aujourd'hui pour
     * une tâche). Le client n'envoie jamais d'instant complet : c'est le serveur qui
     * assemble le jour et l'heure en Europe/Paris (règle R2).
     */
    jour: SchemaJour.nullable().optional(),
    /** L'heure choisie, ou nulle pour « journée entière ». */
    heure: SchemaHeure.nullable().optional(),
    /** La date limite (« avant les vacances ») : le jour est celui où on s'y met, la limite celui où il faut avoir fini. */
    limite: SchemaJour.nullable().optional(),
    personneId: z.uuid().nullable().optional(),
    /** Qui s'en charge. Jamais demandé ; disponible. */
    responsableId: z.uuid().nullable().optional(),
    visibilite: SchemaVisibilite.default('espace'),
    sensibilite: SchemaSensibilite.default('normale'),
});
export const SchemaMajElement = z
    .object({
    titre: SchemaTitre,
    detail: z.string().trim().max(5000).nullable(),
    categorie: SchemaCategorieElement.nullable(),
    /** Nul = retirer la date. Sans « heure », l'heure existante est conservée. */
    jour: SchemaJour.nullable(),
    /** Nul = journée entière. */
    heure: SchemaHeure.nullable(),
    /** Nul = plus de date limite. */
    limite: SchemaJour.nullable(),
    personneId: z.uuid().nullable(),
    responsableId: z.uuid().nullable(),
    visibilite: SchemaVisibilite,
    sensibilite: SchemaSensibilite,
    statut: SchemaStatutElement,
})
    .partial()
    .refine((valeurs) => Object.keys(valeurs).length > 0, { error: 'Rien à modifier.' });
export const SchemaReport = z
    .object({
    quand: SchemaOptionReport,
    /** Obligatoire uniquement quand `quand` vaut « date ». */
    date: SchemaJour.optional(),
})
    .refine((v) => v.quand !== 'date' || v.date !== undefined, {
    error: 'Il manque la date choisie.',
    path: ['date'],
});
export const SchemaElement = z.object({
    id: z.uuid(),
    espaceId: z.uuid(),
    type: SchemaTypeElement,
    titre: z.string(),
    detail: z.string().nullable(),
    statut: SchemaStatutElement,
    categorie: SchemaCategorieElement.nullable(),
    debutLe: SchemaInstant.nullable(),
    finLe: SchemaInstant.nullable(),
    journeeEntiere: z.boolean(),
    /** Le jour où il faut avoir fini, s'il y en a un (« avant les vacances »). */
    limiteLe: SchemaJour.nullable().default(null),
    personneId: z.uuid().nullable(),
    responsableId: z.uuid().nullable(),
    parentId: z.uuid().nullable(),
    visibilite: SchemaVisibilite,
    sensibilite: SchemaSensibilite,
    origine: SchemaOrigineElement,
    confiance: z.number().min(0).max(1).nullable(),
    creePar: z.uuid().nullable(),
    creeLe: SchemaInstant,
    majLe: SchemaInstant,
    faitLe: SchemaInstant.nullable(),
});
/** Ce que l'écran Aujourd'hui affiche : le serveur a déjà tout trié. */
export const SchemaVueAujourdhui = z.object({
    /** Le jour civil courant en Europe/Paris, pour l'en-tête de l'écran. */
    jour: SchemaJour,
    /** Le jour civil de demain : le client ne calcule jamais une date lui-même. */
    jourDemain: SchemaJour,
    aujourdhui: z.array(SchemaElement),
    demain: z.array(SchemaElement),
    sansDate: z.array(SchemaElement),
    /** Les tâches faites aujourd'hui, pour pouvoir annuler un « fait » par erreur. */
    faitesAujourdhui: z.array(SchemaElement),
});
//# sourceMappingURL=elements.js.map